import { useRuntimeConfig } from '#app';

declare global {
   interface Window {
      gtag: (...args: any[]) => void;
      dataLayer: any[];
   }
}

export function useGTM() {
   const config = useRuntimeConfig();

   /**
    * Check if GTM is ready
    */
   const isGTMReady = () => {
      return (
         import.meta.client &&
         typeof window !== 'undefined' &&
         typeof window.gtag === 'function' &&
         Array.isArray(window.dataLayer)
      );
   };

   /**
    * Send a custom event to GTM
    * @param eventName - The name of the event
    * @param parameters - Event parameters
    */
   const sendEvent = (
      eventName: string,
      parameters: Record<string, any> = {}
   ) => {
      if (!isGTMReady()) {
         // console.warn('GTM not ready, queueing event:', eventName)
         // Queue the event for when GTM is ready
         setTimeout(() => sendEvent(eventName, parameters), 100);
         return;
      }

      window.gtag('event', eventName, {
         ...parameters,
         gtm_id: config.public.gtmId,
      });

      // console.log(`📊 GTM Event: ${eventName}`, parameters)
   };

   /**
    * Track page views
    * @param url - The page URL
    * @param title - The page title
    */
   const trackPageView = (url?: string, title?: string) => {
      if (!isGTMReady()) {
         // console.warn('GTM not ready for page view tracking')
         return;
      }

      const pageUrl = url || window.location.pathname;
      const pageTitle = title || document.title;

      // Send page_view event (GA4 standard)
      window.gtag('event', 'page_view', {
         page_title: pageTitle,
         page_location: window.location.href,
         page_path: pageUrl,
      });

      // Also update config for enhanced measurement
      window.gtag('config', config.public.gtmId, {
         page_path: pageUrl,
         page_title: pageTitle,
      });

      // console.log(`📄 Page View: ${pageTitle} (${pageUrl})`)
   };

   /**
    * Update consent for GTM
    * @param consentUpdate - Object with consent settings
    */
   const updateConsent = (
      consentUpdate: Record<string, 'granted' | 'denied'>
   ) => {
      if (!import.meta.client || !window.gtag) return;
      window.gtag('consent', 'update', consentUpdate);
      // console.log('GTM Consent updated:', consentUpdate)
   };

   /**
    * Track user interactions
    * @param action - The action performed
    * @param category - The category of the action
    * @param label - Optional label for the action
    * @param value - Optional numeric value
    */
   const trackInteraction = (
      action: string,
      category: string,
      label?: string,
      value?: number
   ) => {
      sendEvent('interaction', {
         event_category: category,
         event_label: label,
         value: value,
         action: action,
      });
   };

   /**
    * Track video interactions
    * @param action - play, pause, complete, etc.
    * @param videoTitle - Title of the video
    * @param duration - Video duration in seconds
    * @param currentTime - Current playback time
    */
   const trackVideo = (
      action: string,
      videoTitle: string,
      duration?: number,
      currentTime?: number
   ) => {
      sendEvent('video_interaction', {
         video_title: videoTitle,
         video_duration: duration,
         video_current_time: currentTime,
         action: action,
      });
   };

   /**
    * Track search events
    * @param searchTerm - The search query
    * @param category - Type of search (teachables, units, etc.)
    * @param results - Number of results returned
    */
   const trackSearch = (
      searchTerm: string,
      category: string,
      results?: number
   ) => {
      sendEvent('search', {
         search_term: searchTerm,
         search_category: category,
         search_results: results,
      });
   };

   return {
      sendEvent,
      trackPageView,
      updateConsent,
      trackInteraction,
      trackVideo,
      trackSearch,
   };
}
